import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  alpha,
  Box,
  Chip,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountBalance as BudgetIcon,
  Search as SearchIcon,
  TrendingDown as UsedIcon,
  Savings as RemainIcon,
  AttachMoney as CostIcon,
} from '@mui/icons-material';
import * as actions from '../../redux/actions';

const fmt = (n) =>
  n == null ? '-' : Number(n).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const pct = (n) => n == null ? 0 : Math.min(100, Math.max(0, Number(n)));

function UsageBar({ percentage, theme }) {
  const v = pct(percentage);
  const color =
    v >= 90 ? theme.palette.error.main :
    v >= 70 ? theme.palette.warning.main :
    theme.palette.success.main;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={v}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(color, 0.12),
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 38, textAlign: 'right' }}>
        {v.toFixed(1)}%
      </Typography>
    </Box>
  );
}

const STATUS_FILTERS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'open', label: 'เปิดอยู่' },
  { value: 'closed', label: 'ปิดแล้ว' },
];

export default function BudgetList({ title }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const budget = useSelector((s) => s.budget);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    dispatch(actions.budgetAll({ name: '', size: 999999, page: 1, project_id: '' }));
  }, []);

  const rows = budget?.rows || [];
  const isLoading = budget?.isLoading;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((b) => {
      const matchStatus =
        statusFilter === 'all' ||
        (statusFilter === 'open' && !b.expenses_status) ||
        (statusFilter === 'closed' && !!b.expenses_status);
      if (!matchStatus) return false;
      if (!q) return true;
      const proj = b.project;
      return (
        b.budget_number?.toLowerCase().includes(q) ||
        b.name?.toLowerCase().includes(q) ||
        b.prefix?.toLowerCase().includes(q) ||
        b.description?.toLowerCase().includes(q) ||
        proj?.name?.toLowerCase().includes(q) ||
        proj?.project_number?.toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter]);

  const stats = useMemo(() => {
    const totalCost = rows.reduce((s, b) => s + (b.cost || 0), 0);
    const totalUsed = rows.reduce((s, b) => s + (b.total_expenses || 0), 0);
    return { total: rows.length, totalCost, totalUsed, remain: totalCost - totalUsed };
  }, [rows]);

  const STAT_CARDS = [
    { label: 'รายการทั้งหมด', value: stats.total, unit: 'รายการ', color: 'primary', icon: <BudgetIcon /> },
    { label: 'งบประมาณรวม', value: fmt(stats.totalCost), unit: '฿', color: 'info', icon: <CostIcon /> },
    { label: 'ใช้ไปแล้ว', value: fmt(stats.totalUsed), unit: '฿', color: 'warning', icon: <UsedIcon /> },
    { label: 'คงเหลือ', value: fmt(stats.remain), unit: '฿', color: stats.remain < 0 ? 'error' : 'success', icon: <RemainIcon /> },
  ];

  const COL_HEADS = [
    { label: '#', align: 'center', width: 48 },
    { label: 'รหัส', width: 130 },
    { label: 'ชื่องบประมาณ', width: 220 },
    { label: 'โปรเจค', width: 220 },
    { label: 'งบประมาณ (฿)', align: 'right', width: 150 },
    { label: 'ใช้ไปแล้ว (฿)', align: 'right', width: 150 },
    { label: 'คงเหลือ (฿)', align: 'right', width: 150 },
    { label: '%ใช้ไป', width: 160 },
    { label: 'สถานะ', align: 'center', width: 100 },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* ── Page Header ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Box sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BudgetIcon sx={{ fontSize: 30 }} />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {title || 'งบประมาณทั้งหมด'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {rows.length} รายการ · ทุกโปรเจค
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ── Stat cards ── */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}>
          {STAT_CARDS.map((s, i) => (
            <Box
              key={s.label}
              sx={{
                px: 2.5,
                py: 2,
                borderRight: i < 3 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                borderTop: { xs: i >= 2 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none', sm: 'none' },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Box sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette[s.color].main, 0.12),
                  color: `${s.color}.main`,
                  '& svg': { fontSize: 16 },
                }}>
                  {s.icon}
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>{s.label}</Typography>
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="h6" fontWeight={700} color={`${s.color}.main`}>{s.value}</Typography>
                <Typography variant="caption" color="text.disabled">{s.unit}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* ── Filter bar ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="ค้นหา รหัส / ชื่องบ / โปรเจค..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 320 } }}
        />
        <Stack direction="row" spacing={0.8}>
          {STATUS_FILTERS.map((f) => (
            <Chip
              key={f.value}
              label={f.label}
              size="small"
              onClick={() => setStatusFilter(f.value)}
              color={statusFilter === f.value ? 'primary' : 'default'}
              variant={statusFilter === f.value ? 'filled' : 'outlined'}
              sx={{ fontWeight: statusFilter === f.value ? 700 : 400, cursor: 'pointer' }}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto !important' }}>
          แสดง {filtered.length} จาก {rows.length} รายการ
        </Typography>
      </Stack>

      {/* ── Table ── */}
      <TableContainer component={Paper}>
        {isLoading && rows.length > 0 && <LinearProgress sx={{ height: 2 }} />}
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
          <TableHead>
            <TableRow className="bg-theme-600">
              {COL_HEADS.map((h) => (
                <TableCell key={h.label} align={h.align || 'left'} sx={{ width: h.width, whiteSpace: 'nowrap' }}>
                  <h1 className="text-white" style={{ margin: 0, fontWeight: 700, fontSize: '0.78rem' }}>{h.label}</h1>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Skeleton — initial load */}
            {isLoading && rows.length === 0 && Array(6).fill(0).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                <TableCell align="center"><Skeleton width={20} /></TableCell>
                <TableCell><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton width={160} /></TableCell>
                <TableCell><Skeleton width={160} /></TableCell>
                <TableCell align="right"><Skeleton width={80} /></TableCell>
                <TableCell align="right"><Skeleton width={80} /></TableCell>
                <TableCell align="right"><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton variant="rounded" width={120} height={6} /></TableCell>
                <TableCell align="center"><Skeleton variant="rounded" width={60} height={22} /></TableCell>
              </TableRow>
            ))}

            {/* Empty state */}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                  <Stack alignItems="center" spacing={1}>
                    <BudgetIcon sx={{ fontSize: 48, opacity: 0.2 }} />
                    <Typography variant="body2" color="text.secondary">
                      {search ? 'ไม่พบรายการที่ตรงกับคำค้นหา' : 'ยังไม่มีข้อมูลงบประมาณ'}
                    </Typography>
                    {search && (
                      <Typography variant="caption" color="text.disabled">ลองเปลี่ยนคำค้นหาหรือตัวกรอง</Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {/* Data rows */}
            {filtered.map((b, idx) => {
              const remain = (b.cost || 0) - (b.total_expenses || 0);
              const proj = b.project;
              return (
                <TableRow
                  key={b._id || b.id}
                  hover
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.action.hover, 0.03) },
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05), transition: 'background-color 0.15s' },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell align="center" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                    {idx + 1}
                  </TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ fontFamily: 'monospace', color: theme.palette.primary.main }}
                    >
                      {b.budget_number || '-'}
                    </Typography>
                    {b.prefix && (
                      <Typography variant="caption" color="text.disabled">{b.prefix}</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Tooltip title={b.description || ''} placement="top" arrow disableHoverListener={!b.description}>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {b.name || '-'}
                        </Typography>
                        {b.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                          >
                            {b.description}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    {proj ? (
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {proj.name || '-'}
                        </Typography>
                        {proj.project_number && (
                          <Chip
                            label={proj.project_number}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ mt: 0.25, height: 18, fontSize: '0.68rem', borderRadius: 1 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">ไม่ระบุโปรเจค</Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{fmt(b.cost)}</Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500} color="warning.main">
                      {fmt(b.total_expenses)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={remain < 0 ? 'error.main' : 'success.main'}
                    >
                      {fmt(remain)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <UsageBar percentage={b.percentage} theme={theme} />
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={b.expenses_status ? 'ปิดแล้ว' : 'เปิดอยู่'}
                      size="small"
                      color={b.expenses_status ? 'default' : 'success'}
                      variant={b.expenses_status ? 'outlined' : 'filled'}
                      sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
